import React from 'react';
import { Button, Grid } from '@material-ui/core';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
} from '@backstage/plugin-catalog';
import { isGithubActionsAvailable, EntityGithubActionsContent } from '@backstage/plugin-github-actions';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { EmptyState } from '@backstage/core-components';
import { Direction, EntityCatalogGraphCard } from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { GitHubRepoCard } from '../card/GitHubRepoCard';
import { TeamCard } from '../card/TeamCard';
import { ConfluenceSpace } from '../card/ConfluenceSpace';
import { QualityGatesCard } from '../card/QualityGatesCard';
import { JiraProject } from '../card/JiraProject';
import { ProjectInfoCard } from '../card/ProjectInfoCard';
import { ComponentInfoCard } from '../card/ComponentInfoCard';
import { APIInfoCard } from '../card/APIInfoCard';
import { CollaboratorsCard } from '../card/CollaboratorsCard';
import MetricsCard from '../card/MetricsCard';
import { EntityJiraOverviewCard, isJiraAvailable } from '@roadiehq/backstage-plugin-jira';
import { SummarAIzeCard } from '../card/SummarAIzeCard';
import { CustomEntityLayout } from './CustomEntityLayout';
import { StretchItem } from './StretchItem';
import EnvironmentsCard from '../card/EnvironmentsCard';
import { ComponentType } from '@softwareone/plugin-project-management-common/src/types/component-management/componentType';

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const cicdContent = (
  // This is an example of how you can implement your company's logic in entity page.
  // You can for example enforce that all components of type 'service' should use GitHubActions
  <EntitySwitch>
    <EntitySwitch.Case if={isGithubActionsAvailable}>
      <EntityGithubActionsContent />
    </EntitySwitch.Case>

    <EntitySwitch.Case>
      <EmptyState
        title="No CI/CD available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
          >
            Read more
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <StretchItem md={6}>
      <EntityAboutCard />
    </StretchItem>
    <Grid item md={8} xs={12}>
      <EntityHasSubcomponentsCard variant="gridItem" />
    </Grid>
    <StretchItem md={6} xs={12} headerDivider>
      <EntityCatalogGraphCard height={400} />
    </StretchItem>
    <StretchItem md={4} xs={12} headerDivider>
      <EntityLinksCard />
    </StretchItem>
  </Grid>
);

const serviceEntityPage = (
  <CustomEntityLayout>
    <CustomEntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </CustomEntityLayout.Route>

    <CustomEntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </CustomEntityLayout.Route>

    <CustomEntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </CustomEntityLayout.Route>

    <CustomEntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/component-info" title="Component Info">
      <ComponentInfoCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/collaborators" title="Collaborators">
      <CollaboratorsCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/summaraize" title="SummarAIze">
      <SummarAIzeCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/metrics" title="Metrics">
      <MetricsCard />
    </CustomEntityLayout.Route>
  </CustomEntityLayout>
);

const websiteEntityPage = (
  <CustomEntityLayout>
    <CustomEntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </CustomEntityLayout.Route>

    <CustomEntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </CustomEntityLayout.Route>

    <CustomEntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </CustomEntityLayout.Route>

    <CustomEntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </CustomEntityLayout.Route>

    <CustomEntityLayout.Route path="/component-info" title="Component Info">
      <ComponentInfoCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/collaborators" title="Collaborators">
      <CollaboratorsCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/summaraize" title="SummarAIze">
      <SummarAIzeCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/metrics" title="Metrics">
      <MetricsCard />
    </CustomEntityLayout.Route>
  </CustomEntityLayout>
);

const libraryEntityPage = (
  <CustomEntityLayout>
    <CustomEntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </CustomEntityLayout.Route>

    <CustomEntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </CustomEntityLayout.Route>

    <CustomEntityLayout.Route path="/component-info" title="Component Info">
      <ComponentInfoCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/collaborators" title="Collaborators">
      <CollaboratorsCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/summaraize" title="SummarAIze">
      <SummarAIzeCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/metrics" title="Metrics">
      <MetricsCard />
    </CustomEntityLayout.Route>
  </CustomEntityLayout>
);

const otherEntityPage = (
  <CustomEntityLayout>
    <CustomEntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </CustomEntityLayout.Route>

    <CustomEntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </CustomEntityLayout.Route>

    <CustomEntityLayout.Route path="/component-info" title="Component Info">
      <ComponentInfoCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/collaborators" title="Collaborators">
      <CollaboratorsCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/summaraize" title="SummarAIze">
      <SummarAIzeCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/metrics" title="Metrics">
      <MetricsCard />
    </CustomEntityLayout.Route>
  </CustomEntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <CustomEntityLayout>
    <CustomEntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </CustomEntityLayout.Route>

    <CustomEntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </CustomEntityLayout.Route>
  </CustomEntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType(ComponentType.SERVICE)}>{serviceEntityPage}</EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType(ComponentType.WEBSITE)}>{websiteEntityPage}</EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType(ComponentType.LIBRARY)}>{libraryEntityPage}</EntitySwitch.Case>

    <EntitySwitch.Case
      if={isComponentType([
        ComponentType.WEB_APPLICATION,
        ComponentType.MOBILE_APPLICATION,
        ComponentType.BUSINESS_INTELLIGENCE,
        ComponentType.MACHINE_LEARNING,
        ComponentType.AI,
        ComponentType.FUNCTION,
        ComponentType.LOW_CODE,
        ComponentType.CMS,
        ComponentType.CRM,
        ComponentType.CLI,
        ComponentType.INFRASTRUCTURE_AS_CODE,
        ComponentType.AUTOMATED_TESTING,
        ComponentType.DOCUMENTATION,
        ComponentType.OTHER,
      ])}
    >
      {otherEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <CustomEntityLayout>
    <CustomEntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <StretchItem md={6}>
          <EntityAboutCard />
        </StretchItem>
        <StretchItem md={6} xs={12} headerDivider>
          <EntityCatalogGraphCard height={400} />
        </StretchItem>
        <StretchItem md={4} xs={12} headerDivider>
          <EntityLinksCard />
        </StretchItem>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </CustomEntityLayout.Route>

    <CustomEntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/api-info" title="API Info">
      <APIInfoCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/collaborators" title="Collaborators">
      <CollaboratorsCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/metrics" title="Metrics">
      <MetricsCard />
    </CustomEntityLayout.Route>
  </CustomEntityLayout>
);

const userPage = (
  <CustomEntityLayout>
    <CustomEntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <StretchItem xs={12} md={6}>
          <EntityUserProfileCard />
        </StretchItem>
        <StretchItem xs={12} md={6}>
          <EntityOwnershipCard />
        </StretchItem>
      </Grid>
    </CustomEntityLayout.Route>
  </CustomEntityLayout>
);

const groupPage = (
  <CustomEntityLayout>
    <CustomEntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <StretchItem xs={12} md={6}>
          <EntityGroupProfileCard />
        </StretchItem>
        <StretchItem xs={12} md={6}>
          <EntityOwnershipCard />
        </StretchItem>
        <Grid item xs={12}>
          <EntityMembersListCard />
        </Grid>
      </Grid>
    </CustomEntityLayout.Route>
  </CustomEntityLayout>
);

const systemPage = (
  <CustomEntityLayout>
    <CustomEntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <StretchItem md={6}>
          <EntityAboutCard />
        </StretchItem>
        <StretchItem md={6} xs={12} headerDivider>
          <EntityCatalogGraphCard height={400} />
        </StretchItem>
        <StretchItem md={4} xs={12} headerDivider>
          <EntityLinksCard />
        </StretchItem>
        <Grid item md={8}>
          <EntityHasComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard variant="gridItem" />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case if={isJiraAvailable}>
            <Grid item md={12}>
              <EntityJiraOverviewCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/project-info" title="Project Info">
      <ProjectInfoCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/github-repositories" title="GitHub Repositories">
      <GitHubRepoCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/project-members" title="Team">
      <TeamCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/jira-project-members" title="Jira Project">
      <JiraProject />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/confluence-space" title="Confluence Space">
      <ConfluenceSpace />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/environments" title="Environments">
      <EnvironmentsCard />
    </CustomEntityLayout.Route>
    <CustomEntityLayout.Route path="/quality-gates" title="Quality Gates">
      <QualityGatesCard />
    </CustomEntityLayout.Route>
  </CustomEntityLayout>
);

const domainPage = (
  <CustomEntityLayout>
    <CustomEntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <StretchItem md={6}>
          <EntityAboutCard />
        </StretchItem>
        <StretchItem md={6} xs={12} headerDivider>
          <EntityCatalogGraphCard height={400} />
        </StretchItem>
        <Grid item md={6}>
          <EntityHasSystemsCard variant="gridItem" />
        </Grid>
      </Grid>
    </CustomEntityLayout.Route>
  </CustomEntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
